import * as React from 'react';
import { WebAPI } from '../../MedicareProAPI/API/WebAPI';
import type { UserSunfire } from '../../MedicareProAPI/Entities/User/UserSunfire';
import { UserRepository } from '../../MedicareProAPI/Repositories/User/UserRepository';
import StyledModal from '../Components/StyledModal';

export interface SunFireEnrollButtonProps {
  userId: number;
  userAccessToken: string;
  indId: number;
  thisUserSunFire?: UserSunfire[];
  stack?: boolean;
}

type Status = 'idle' | 'checking' | 'choice' | 'makingSession' | 'posting' | 'done' | 'error';

/**
 * Modal for interacting with SunFire matrix session/ prospect post
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-10-07
 * @lastUpdated
 * @version 1.1.0
 */
export const SunFireEnrollButton = (props: SunFireEnrollButtonProps): React.JSX.Element => {
  const userRepository = React.useMemo(() => {
    const r = new UserRepository();
    r.setAccessToken(props.userAccessToken);
    return r;
  }, [props.userAccessToken]);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const LS_KEY_MFA_NOTE = 'mpcrm.hideSunfireMfaNote';
  const [hideSunfireMfaNote, setHideSunfireMfaNote] = React.useState(false);

  const [modalKey, setModalKey] = React.useState(0);
  const [modalOpen, setModalOpen] = React.useState<boolean>(false);

  const [userSunFire, setUserSunfire] = React.useState<Array<UserSunfire>>(props.thisUserSunFire);
  const [userSunFireSelected, setUserSunFireSelected] = React.useState<UserSunfire>();

  const [sessionId, setSessionId] = React.useState<string>('');
  const [useExistingSession, setUseExistingSession] = React.useState<boolean>(false);
  const [sessionResponse, setSessionResponse] = React.useState<any>();

  const [prospectPostUrl, setProspectPostUrl] = React.useState<string>('');

  const [status, setStatus] = React.useState<Status>('idle');
  const [error, setError] = React.useState<string | null>(null);

  const [showIntegrationInfo, setShowIntegrationInfo] = React.useState<boolean>(false);
  const [showChooseInfo, setShowChooseInfo] = React.useState<boolean>(false);

  const canMakeSessionOrg = new Set(['tbi']);

  const ALL_CAN_POST_OPTIONS = [
    'Name',
    'DOB',
    'Email',
    'Phone',
    'Address',
    'MBI',
    'Medicaid',
    'Rx',
    'Pharmacies',
    'Providers',
  ];

  const org = userSunFireSelected?.getSunfireOrg?.();
  const canPostArr = org?.getCanPost?.() || [];
  const canPostSet = new Set(canPostArr.map(String));

  /**
   * Resets all SunFire enrollment/session UI state back to initial values.
   *
   * Side effects:
   * - Clears the selected SunFire account (`setUserSunFireSelected(undefined)`).
   * - Empties the current session id (`setSessionId('')`).
   * - Sets status back to `'idle'`.
   * - Clears any error (`setError(null)`).
   * - Clears the last session response (`setSessionResponse(null)`).
   * - Clears any generated prospect post URL (`setProspectPostUrl('')`).
   * - Disables the "use existing session" flag (`setUseExistingSession(false)`).
   *
   * @returns {void}
   * @example
   * // After closing the modal:
   * handleClose = () => {
   *   setModalOpen(false);
   *   reset();
   * };
   */
  const reset = React.useCallback((): void => {
    setUserSunFireSelected(undefined);
    setSessionId('');
    setStatus('idle');
    setError(null);
    setSessionResponse(null);
    setProspectPostUrl('');
    setUseExistingSession(false);
  }, []);

  /**
   * Opens the enrollment modal. If exactly one SunFire account exists,
   * preselect it for the user.
   *
   * @returns {void}
   *
   * @example
   * // On button click
   * <button onClick={handleStartEnrollClick}>Start Enrollment</button>
   */
  const handleStartEnrollClick = () => {
    setModalOpen(true);
    console.log(userSunFire?.length);
    if (userSunFire?.length === 1) {
      setUserSunFireSelected(userSunFire[0]);
    }
  };

  /**
   * Closes the modal, resets related state, and increments the modal key
   * to force an unmount/mount cycle (useful for clearing internal component state).
   *
   * Side effects:
   * - Calls `setModalOpen(false)` to hide the modal.
   * - Invokes `reset()` to clear enrollment/session state.
   * - Bumps `modalKey` via `setModalKey(k => k + 1)` to remount the modal subtree.
   *
   * @returns {void}
   *
   * @example
   * <StyledModal open={modalOpen} onClose={handleClose} />
   */
  const handleClose = () => {
    setModalOpen(false);
    reset();
    setModalKey((k) => k + 1);
  };

  /**
   * Fetches an existing SunFire session/customer code, if available.
   *
   * @async
   * @returns {Promise<string|null>} Non-empty session ID or `null`.
   */
  const fetchExistingSession = async (): Promise<string | null> => {
    const raw = await webAPI.getSunFireCustomerCode(props.userId, { indId: props.indId });
    const val = typeof raw === 'string' ? raw.trim() : '';

    return val ? val : null;
  };

  /**
   * Creates (or resumes) a SunFire session for the current user/individual.
   *
   * Behavior:
   * - Builds params from the selected SunFire org and `props.indId`.
   * - If `useExistingSession` is true and `sessionId` is present, includes it to resume.
   * - Calls `webAPI.createSunFireSession`.
   * - Stores the raw response in state via `setSessionResponse`.
   * - Notifies admin if `session.errors.drugs` contains entries.
   * - Extracts and returns a trimmed `customerCode` (`newId`) or `null` if missing/empty.
   * - Syncs `sessionId` state if `newId` is present.
   *
   *
   * @async
   * @returns {Promise<{session: any, newId: string|null}>} The raw API response and the normalized new session ID.
   *
   * @example
   * const { session, newId } = await makeSession();
   * if (newId) {
   *   // proceed with UI that depends on a live SunFire session
   * }
   */
  const makeSession = async (useExisting?: boolean): Promise<any> => {
    const params = {
      partnerId: userSunFireSelected.getSunfireOrg().getPartnerId(),
      indId: props.indId,
    };

    if (sessionId && useExisting) {
      params['customerCode'] = sessionId;
    }

    const session = await webAPI.createSunFireSession(props.userId, params);
    setSessionResponse(session);

    // New session response
    if (session?.drugErrors?.length > 0) {
      notifyAdminOfErrors(session.drugErrors);
    }

    const newId = session?.session?.customerCode ?? null;
    if (newId) {
      setSessionId(newId);
    }

    return { session, newId };
  };

  /**
   * Creates a SunFire prospect post and stores its redirect URL (if any).
   *
   * @param {*} _sessionData - unused; kept for signature compatibility
   * @param {string|null} [sessionIdOverride]
   * @returns {Promise<void>}
   */
  const makeProspectPost = async (
    sessionData: any | null,
    sessionIdOverride?: string | null
  ): Promise<void> => {
    const orgRef = userSunFireSelected.getSunfireOrg().getReference();

    const payload: any = { indId: props.indId };

    // Prefer the fresh id passed in; fall back to state if allowed
    const effectiveSessionId = sessionIdOverride ?? sessionId;
    if (effectiveSessionId && canMakeSessionOrg.has(orgRef)) {
      payload.sessionId = effectiveSessionId;
    }

    const prospectPost = await webAPI.createSunFireProspectPost(orgRef, payload);

    if (prospectPost?.url) {
      setProspectPostUrl(prospectPost.url);
    } else {
      setProspectPostUrl('');
      setError('Unable to generate redirect URL.');
    }
  };

  /**
   * Starts the SunFire enrollment flow:
   * - Opens modal and clears errors.
   * - If the org supports sessions, tries to reuse an existing one; otherwise creates a new session.
   * - Creates a prospect post and updates status through each phase.
   *
   * Status flow: 'checking' → ('choice' | 'makingSession' → 'posting') → 'done' | 'error'
   *
   * @returns {Promise<void>}
   */
  const startEnroll = async () => {
    if (!userSunFireSelected) return;
    setModalOpen(true);
    setError(null);
    setStatus('checking');

    try {
      const orgRef = userSunFireSelected.getSunfireOrg().getReference();

      let existing = null;
      console.log(canMakeSessionOrg);
      console.log(orgRef);
      // Only check for existing sessions if the Org is in the set
      if (canMakeSessionOrg.has(orgRef)) {
        existing = await fetchExistingSession();
      }

      if (existing) {
        // Found an existing session to use
        setSessionId(existing);
        setStatus('choice'); // user will choose to use the found session, or start new
        return;
      }

      if (canMakeSessionOrg.has(orgRef)) {
        // Org supports sessions but didn't find any for ind, make a new session and prospect post
        setStatus('makingSession');
        const { newId } = await makeSession();

        setStatus('posting');
        await makeProspectPost(null, newId);
      } else {
        // Org doesn't support sessions, make prospect post
        setStatus('posting');
        await makeProspectPost(null);
      }

      setStatus('done');
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
      setStatus('error');
    }
  };

  /**
   * Proceeds after the user chooses whether to reuse an existing session.
   * Creates (or resumes) a SunFire session, then creates a prospect post.
   *
   * Notes:
   * - `setUseExistingSession` is asynchronous; if you need to guarantee the flag
   *   is applied before `makeSession` reads it, consider adding a parameter to
   *   `makeSession` (e.g., `{ reuseExisting: boolean }`) and pass `useExisting` directly.
   *
   * Status flow: 'makingSession' → 'posting' → 'done' | 'error'
   *
   * @param {boolean} useExisting - Whether to reuse an existing session if present.
   * @returns {Promise<void>}
   */
  const chooseExisting = async (useExisting: boolean) => {
    // If we are here, the org already support session
    if (!userSunFireSelected) return;
    setError(null);
    setUseExistingSession(useExisting);
    try {
      setStatus('makingSession');
      const { newId } = await makeSession(useExisting);

      setStatus('posting');
      await makeProspectPost(null, newId);

      setStatus('done');
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
      setStatus('error');
    }
  };

  /**
   * Sends an admin notification email summarizing SunFire drug NDC errors.
   *
   * @param {unknown[]} items - Error items passed to `renderDrugErrorsText` to build the email body.
   * @returns {Promise<void>} Resolves after the email request is issued.
   *
   * @example
   * if (Array.isArray(errors?.drugs) && errors.drugs.length) {
   *   await notifyAdminOfErrors(errors.drugs);
   * }
   */
  const notifyAdminOfErrors = async (items: unknown[]): Promise<void> => {
    const bodyText = String(renderDrugErrorsText(items) ?? '');
    if (bodyText == '') return;
    const emailData = {
      to: 'notifications@medicareprocrm.com',
      from: 'mpcrmmail@gmail.com',
      subject: 'Repair Drug NDC for SunFire',
      templateName: 'NotificationToAdmin',
      templateValues: { body: bodyText },
    };

    try {
      await webAPI.sendEmail(emailData);
    } catch (e: any) {
      console.error('Failed to send admin notification:', e?.message ?? e);
    }
  };

  /**
   * Builds an HTML snippet summarizing drug (Rx) errors for email/rendering.
   *
   * Safeguards:
   * - Treats missing fields as placeholders.
   * - Escapes HTML to avoid injection in names/messages.
   *
   * @param {Array<{ ndc?: string; name?: string; message?: string }>} items
   * @returns {string} HTML string (empty if no items)
   *
   * @example
   * const html = renderDrugErrorsText(errors.drugs);
   * // use in email templateValues.body
   */
  const renderDrugErrorsText = (items) => {
    /*
        00247192300 — metoprolol  TOO MANY
        13668063405 — entresto TOO MANY
        60258016001 — rena-vite  FAILED
        00245041001 — acetaminophen / HYDROcodone TOO MANY
        59310011720 — albuterol  FAILED
        00955172801 — basaglar FAILED
        00002940101 — donanemab-azbt FAILED
        42794021201 — nitroglycerin TOO MANY
        00363403021 — meclizine TOO MANY
        11695696501 — levothyroxine FAILED
        00615837539 — ARIPiprazole  TOO MANY
        63402073760 — zetonna  FAILED
        46708034810 — xarelto FAILED
        00115172201 — apixaban FAILED
        00615840005 — potassium chloride TOO MANY
        66993016897 — fluticasone furoate ellipta  FAILED
        00615844739 — OXcarbazepine TOO MANY
        68083056301 — vyzulta FAILED
        62559074301 — armour thyroid FAILED
        00247049730 — prochlorperazine TOO MANY
        00067067410 — loratadine TOO MANY
        00944271820 — hyqvia 1600 unt per 10 ml / 20 g per 200 ml (10 % ) kit  FAILED
        40985025073 — ergocalciferol FAILED
        63481052910 — fluocinonide / neomycin FAILED
        00615856939 — ranolazine TOO MANY
        00247216030 — glipiZIDE TOO MANY
        63304000830 — metoprolol succinate FAILED
        00054082925 — progesterone TOO MANY
        00247020303 — indomethacin TOO MANY
        65841084614 — topiramate FAILED
        00093724796 — LaMICtal TOO MANY
        27241020630 — rexulti FAILED
        00615823905 — sevelamer carbonate TOO MANY
        00363004801 — budesonide FAILED
        00378534001 — cartia TOO MANY
        00955172901 — Lantus FAILED

        */
    const knownFails = [
      '00247192300',
      '13668063405',
      '60258016001',
      '00245041001',
      '59310011720',
      '00955172801',
      '00002940101',
      '42794021201',
      '00363403021',
      '11695696501',
      '00615837539',
      '63402073760',
      '46708034810',
      '00115172201',
      '00615840005',
      '66993016897',
      '00615844739',
      '68083056301',
      '62559074301',
      '00247049730',
      '00067067410',
      '00944271820',
      '40985025073',
      '63481052910',
      '00615856939',
      '00247216030',
      '63304000830',
      '00054082925',
      '00247020303',
      '65841084614',
      '00093724796',
      '27241020630',
      '00615823905',
      '00363004801',
      '00378534001',
      '00955172901',
    ];

    const otcNdcs = [
      '21130048112',
      '37864075010',
      '00179805002',
      '00113036862',
      '00761051612',
      '00363036142',
      '00363025514',
      '39328004715',
      '00113098163',
      '00113019402',
      '00113945813',
      '00113057122',
      '00761016840',
      '00113690002',
      '00113025968',
      '00113004471',
      '00761015540',
      '00363071101',
      '00067486007',
      '00179830201',
      '00113048672',
      '00363403021',
      '00363012010',
      '37205098651',
      '00067067410',
      '72127090401',
      '00113047847',
      '00363004420',
      '00536100901',
      '50580013008',
      '00113042553',
      '00280009201',
      '10542010803',
      '07431251239',
      '00615831039',
    ];

    if (!Array.isArray(items) || items.length === 0) return '';

    const esc = (v) =>
      String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const normalizeNdc = (v) => String(v ?? '').replace(/\D+/g, ''); // digits-only

    // Build a quick lookup set of normalized OTC NDCs
    const otcSet = new Set(
      (Array.isArray(otcNdcs) ? otcNdcs : []).map(normalizeNdc).filter(Boolean)
    );

    const knownFailsSet = new Set(
      (Array.isArray(knownFails) ? knownFails : []).map(normalizeNdc).filter(Boolean)
    );

    const filtered = items.filter((d) => {
      const ndcDigits = normalizeNdc(d?.ndc);
      if (!ndcDigits) return true; // no NDC, keep it

      if (otcSet.has(ndcDigits)) return false;
      if (knownFailsSet.has(ndcDigits)) return false;

      return true;
    });

    // If everything was filtered out, return empty string (no text)
    if (filtered.length === 0) return '';

    const rows = filtered
      .map((d) => {
        const ndc = d?.ndc ? `NDC ${esc(d.ndc)}` : 'NDC —';
        const name = d?.name ? esc(d.name) : '(unknown drug)';
        return `<div style="margin-top:6px;">• ${ndc} — ${name}</div>`;
      })
      .join('');

    return `
            <div><strong>⚠️ The following Rx were not sent:</strong></div>
            ${rows}
        `;
  };

  const handleHideMfaNoteChange = (e) => {
    const checked = e.target.checked;
    setHideSunfireMfaNote(checked);
    try {
      if (checked) localStorage.setItem(LS_KEY_MFA_NOTE, '1');
      else localStorage.removeItem(LS_KEY_MFA_NOTE);
    } catch (_) {}
  };

  React.useEffect(() => {
    try {
      setHideSunfireMfaNote(localStorage.getItem(LS_KEY_MFA_NOTE) === '1');
    } catch (_) {}
  }, []);

  /**
   * When the modal opens with a selected SunFire account, kick off enrollment.
   *
   * Triggers:
   * - Runs whenever `modalOpen` or `userSunFireSelected` changes.
   * - If the modal isn’t open or no account is selected, it exits early.
   *
   * Side effects:
   * - Invokes `startEnroll()` which manages status and posting flow.
   */
  React.useEffect(() => {
    if (!modalOpen || !userSunFireSelected) return;
    startEnroll();
  }, [modalOpen, userSunFireSelected]);

  return (
    <>
      {userSunFire.length > 0 && !props.stack && (
        <div
          className="btn btn-sm btn-white mb-3 mr-2 float-right"
          onClick={handleStartEnrollClick}
        >
          <img
            style={{ height: '20px' }}
            className="mr-1"
            alt="Sunfire Matrix"
            src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/sunfireicon.png"
          />
          <b>Enroll</b>
        </div>
      )}

      {userSunFire.length > 0 && props.stack && (
        <div
          className={`btn border-orange text-orange btn-sm p-2 mt-2 d-block mx-auto cp px-3 hover-shadow `}
          style={{ borderRadius: '15px' }}
          onClick={handleStartEnrollClick}
        >
          <img
            className="mr-2"
            style={{ height: '20px' }}
            alt="SunFire Matrix"
            src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/sunfireicon.png"
          />
          <b>SunFire</b>
        </div>
      )}
      {modalOpen && (
        <StyledModal
          key={modalKey}
          size="lg"
          title="Sunfire"
          footerCloseButton
          show={true}
          onClose={handleClose}
        >
          {userSunFire.length > 1 && !userSunFireSelected && (
            <div className="row">
              <div className="col-12">
                <div className="mb-2">
                  <b>Choose SunFire Org</b>
                </div>
                {userSunFire.map((item, idx) => (
                  <div
                    className="rounded bg-contrast p-2 mb-2 cp"
                    onClick={() => setUserSunFireSelected(item)}
                  >
                    <img
                      style={{ height: '35px' }}
                      alt="Sunfire Matrix"
                      src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/sunfireicon.png"
                    />
                    <span className="ml-2">
                      <b>{item.getSunfireOrg().getOrgName()}</b>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {userSunFireSelected && (
            <div className="row">
              {/* left column */}
              <div className="col-12 col-lg-8 text-center align-self-center">
                {status === 'checking' && (
                  <div>
                    <b>
                      <i className="far fa-spinner text-orange fa-spin" /> Checking for existing
                      session…
                    </b>
                  </div>
                )}

                {status === 'choice' && (
                  <>
                    <div>
                      <i className="fas fa-check-circle text-orange" />{' '}
                      <b>Existing session found</b>
                    </div>

                    <div
                      className={`btn border-orange text-orange btn-sm p-2 mt-2 cp mt-5 px-3 hover-shadow `}
                      onClick={() => chooseExisting(true)}
                      style={{ borderRadius: '15px' }}
                    >
                      <b>
                        Continue with session{' '}
                        <span className="badge badge-orange">{sessionId}</span>{' '}
                        <i className="far fa-arrow-alt-circle-right ml-2"></i>
                      </b>{' '}
                      <span className="dataBadge"></span>
                    </div>

                    <div className="mb-2 mt-2">
                      <b>OR</b>
                    </div>
                    <div className="mb-2 mt-2">
                      <div
                        className={`btn border-orange text-orange btn-sm p-2 d-inline-block cp px-3 hover-shadow `}
                        onClick={() => chooseExisting(false)}
                        style={{ borderRadius: '15px' }}
                      >
                        <b>
                          Start new session <i className="far fa-arrow-alt-circle-right ml-2"></i>
                        </b>
                      </div>
                    </div>
                    {!showChooseInfo && (
                      <div className="mt-5 text-left">
                        <div
                          className="badge border-secondary cp"
                          onClick={() => setShowChooseInfo(!showChooseInfo)}
                        >
                          <i className="fas fa-question-circle mr-1"></i>Help me decide
                        </div>
                      </div>
                    )}

                    {showChooseInfo && (
                      <div className="rounded p-3 bg-contrast small p-2 mt-5 text-left">
                        <i className="fas fa-info-circle mr-1" />
                        <b>Choose how to proceed:</b>
                        <ul className="mb-0 mt-2 pl-3">
                          <li>
                            <b>Use existing session</b> — MPCRM will <b>replace</b> the data on this
                            SunFire profile with what it sends now (keeps the current
                            profile/session ID).
                          </li>
                          <li className="mt-1">
                            <b>Start new session</b> — creates a <b>new SunFire profile</b> with a
                            <b> new session ID</b>.
                          </li>
                        </ul>
                        <div
                          className="badge badge-secondary cp mt-2"
                          onClick={() => setShowChooseInfo(!showChooseInfo)}
                        >
                          Close
                        </div>
                      </div>
                    )}
                  </>
                )}

                {status === 'makingSession' && (
                  <div>
                    <b>
                      <i className="far fa-spinner text-orange fa-spin" /> Creating session…
                    </b>
                  </div>
                )}

                {status === 'posting' && (
                  <div>
                    <b>
                      <i className="far fa-spinner text-orange fa-spin" /> Sending to SunFire...
                    </b>
                  </div>
                )}

                {status === 'done' && (
                  <>
                    {!hideSunfireMfaNote && (
                      <div className="p-2 rounded bg-contrast mb-3" style={{ fontSize: '10pt' }}>
                        <div className="mb-2">
                          <b>Important info</b>
                        </div>
                        <ul className="text-left">
                          <li>
                            When you land on the SunFire dashboard it wont be obvious that any data
                            has transferred over. You need to click <b>"Quote & Enroll"</b> and it
                            will load this profile.
                          </li>
                          {canMakeSessionOrg.has(org.getReference()) && (
                            <li className="mt-2">
                              <b>FYI</b>: SunFire can occasionally drop session data (Rx,
                              pharmacies) if you are prompted to log in. If that happens, just
                              return to this screen and run the process again after logging in.
                            </li>
                          )}
                        </ul>
                        <div className="form-check mt-2 text-left">
                          <input
                            id="hideSunfireMfaNote"
                            type="checkbox"
                            className="form-check-input"
                            onChange={handleHideMfaNoteChange}
                            checked={hideSunfireMfaNote}
                          />
                          <label className="form-check-label small" htmlFor="hideSunfireMfaNote">
                            Don’t show this again
                          </label>
                        </div>
                      </div>
                    )}

                    <a
                      className={`btn border-orange text-orange btn-sm p-2 d-inline-block cp px-3 hover-shadow ${!prospectPostUrl ? 'disabled' : ''}`}
                      href={prospectPostUrl ?? '#'}
                      style={{ borderRadius: '15px' }}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!prospectPostUrl) e.preventDefault();
                      }}
                      aria-disabled={!prospectPostUrl}
                    >
                      <img
                        className="mr-2"
                        style={{ height: '20px' }}
                        alt="Sunfire Matrix"
                        src="https://medicareprocrm-cdn.s3.us-east-2.amazonaws.com/public/img/site/sunfireicon.png"
                      />
                      <b>Continue to SunFire</b>{' '}
                      <i className="far fa-arrow-alt-circle-right ml-2"></i>
                    </a>
                  </>
                )}

                {(sessionResponse?.drugErrors?.length ?? 0) > 0 && (
                  <div className="mt-2 rounded bg-contrast p-3 small mt-5 text-left">
                    <div className="text-danger mb-2" aria-hidden="true">
                      <b>
                        <i className="far fa-exclamation-circle mr-2"></i>The following Rx were not
                        sent
                      </b>
                    </div>
                    {sessionResponse.drugErrors.map((item, idx) => (
                      <div key={idx} className="mt-2">
                        <i className="fas fa-capsules mr-2"></i>
                        {item.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* right column (sibling, not nested inside col-8) */}
              <div className="col-12 col-lg-4">
                {userSunFireSelected?.getSunfireOrg?.() && (
                  <>
                    <div className="mb-2">
                      What we can send to SunFire through{' '}
                      <b>{userSunFireSelected.getSunfireOrg().getOrgName()}</b>
                    </div>
                    {ALL_CAN_POST_OPTIONS.map((opt) => {
                      const allowed = canPostSet.has(String(opt));
                      return (
                        <span
                          key={opt}
                          className={`badge dataBadge text-dark mr-2 cp ${allowed ? 'table-success' : 'table-danger'}`}
                          title={allowed ? 'Included' : 'Not included'}
                          onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                        >
                          <i className={`mr-1 far ${allowed ? 'fa-check' : 'fa-times'}`}></i>
                          {opt}
                        </span>
                      );
                    })}

                    <div className="mb-2 mt-4">
                      What SunFire <b>sends back</b> after an <b>enrollment</b>
                    </div>
                    {(userSunFireSelected.getSunfireOrg().getCanIngest?.() || []).map(
                      (item, idx) => (
                        <span
                          key={idx}
                          className="badge table-success dataBadge text-dark mr-2 cp"
                          onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                        >
                          <i className={`mr-1 far fa-check`}></i>
                          {item}
                        </span>
                      )
                    )}
                    {(userSunFireSelected.getSunfireOrg().getCanIngest?.() || []).length === 0 && (
                      <span
                        key={'no-sync-sunfire'}
                        onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                        className="badge table-danger text-dark dataBadge mr-2 cp"
                      >
                        no sync
                      </span>
                    )}

                    <div className="mt-2">
                      <div
                        className="badge border-secondary cp"
                        onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                      >
                        <i className="fas fa-question-circle mr-1"></i>How this works
                      </div>
                    </div>
                  </>
                )}
              </div>
              {showIntegrationInfo && (
                <div className="col-12 mt-2">
                  <div className="mt-2 rounded bg-contrast p-3 small">
                    <div className="mb-2">
                      <b>How SunFire settings affect data we can send</b>
                    </div>
                    <ul>
                      <li>
                        We can always send basic application info to <b>any SunFire instance</b> as
                        long as that org has given us access to do so.
                      </li>
                      <li>
                        To send <b>Rx and pharmacy data</b>, the agency/FMO’s SunFire instance must
                        have <b>API access</b> enabled, and those API credentials shared with MPCRM.
                      </li>
                      <li>
                        For data to flow back into MPCRM (e.g., plan data, providers, etc), that
                        SunFire instance must have <b>enrollment and session webhooks</b> enabled
                        and <b>pointed to MPCRM.</b>
                      </li>
                      <li>
                        <b>We can’t send provider data to any SunFire instance.</b> MPCRM and
                        SunFire use different provider directories/IDs, so providers can’t be
                        matched reliably. Please add providers directly in SunFire.
                      </li>
                    </ul>
                    <div
                      className="badge badge-secondary cp"
                      onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                    >
                      Close
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </StyledModal>
      )}
    </>
  );
};
